"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { ApaTable } from "@/lib/tool-kit/stats-regression/apa-table";
import type { MatrixReport } from "@/lib/tool-kit/stats-regression/correlation-matrix";

/*
 * Tables for the correlation-matrix widget. Every word is passed in; these components own
 * layout and colour only.
 *
 * The heat colours are the colour-blind-safe blue and vermillion of the chart palette, so
 * the sign of a correlation is told apart without relying on red against green; the
 * printed value is always in the cell, so colour is never the only cue.
 */

const POSITIVE = "0, 114, 178";
const NEGATIVE = "213, 94, 0";

/** A translucent fill whose strength is |r|, so it reads on light and dark pages. */
export function heatStyle(r: number): { backgroundColor?: string } {
  if (Number.isNaN(r)) {
    return {};
  }
  const alpha = Math.min(1, Math.abs(r)) * 0.5;
  return {
    backgroundColor: `rgba(${r >= 0 ? POSITIVE : NEGATIVE}, ${alpha.toFixed(3)})`,
  };
}

const FIXED_COLUMNS = 3;

/**
 * The APA table, with each lower-triangle cell shaded by its correlation.
 * `table` is the text; `report.r` supplies the shading.
 */
export function HeatApaTable({
  table,
  report,
  caption,
}: {
  table: ApaTable;
  report: MatrixReport;
  caption?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={`${TABLE} [overflow-wrap:normal]`}>
        {caption ? (
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {caption}
          </caption>
        ) : null}
        <thead>
          <tr>
            {table.head.map((cell, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: numbered headers can repeat
              <th className={`${TH} align-bottom`} key={index} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <tr key={i}>
              {row.map((cell, index) => {
                const j = index - FIXED_COLUMNS;
                const shaded = j >= 0 && j < i;
                return (
                  <td
                    className={`${TD} whitespace-nowrap tabular-nums ${index === 0 ? "min-w-32 font-medium" : ""}`}
                    // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                    key={index}
                    style={shaded ? heatStyle(report.r[i][j]) : undefined}
                  >
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
